export const RaceSeries = {
  b7: {
    name: "Beskydská sedmička",
    key: "b7",
  },
  lh24: {
    name: "Lysá hora 24",
    key: "lh24",
  },
  grizzly: {
    name: "Grizzlyman",
    key: "grizzly",
  },
};

/** Default logo filename per series (public/race-logos/), used when race.logoUrl is not set */
export const RaceSeriesDefaultLogos = {
  b7: "b7-logo.svg",
  lh24: "lh24-logo.svg",
  grizzly: "grizzly-logo.svg",
};

/**
 * Get public URL for a series default logo
 * @param {string} seriesKey - Race series key (b7, lh24, grizzly)
 * @returns {string} Public URL or empty string
 */
export function getSeriesLogoUrl(seriesKey) {
  const filename = RaceSeriesDefaultLogos[seriesKey?.toLowerCase()];
  if (!filename) return "";
  const base = import.meta.env.BASE_URL || "/";
  return `${base}race-logos/${filename}`;
}

/**
 * Default public links for a new race, per race series.
 * A race can override rules and GDPR with its own rulesLink and gdprLink.
 * An empty logoUrl keeps the series logo from {@link RaceSeriesDefaultLogos}.
 */
export const RaceSeriesDefaultLinks = {
  b7: {
    url: "https://www.beskydskasedmicka.cz/",
    logoUrl: "https://beskydskasedmicka.cz/logo_b7.png",
    rules: "https://www.beskydskasedmicka.cz/pravidla-zavodu/",
    gdpr: "https://www.beskydskasedmicka.cz/gdpr/",
  },
  lh24: {
    url: "https://lh24.cz/",
    logoUrl: "https://lh24.cz/logo_lh24.png",
    rules: "https://lh24.cz/pravidla/",
    gdpr: "https://lh24.cz/gdpr/",
  },
  grizzly: {
    url: "https://grizzlyman.cz/",
    logoUrl: "https://grizzlyman.cz/wp-content/themes/grizzlyman/images/symbol_black.png",
    rules: "https://grizzlyman.cz/propozice-grizzlyman/",
    gdpr: "https://grizzlyman.cz/gdpr/",
  },
};

const emptySeriesDefaultLinks = { url: "", logoUrl: "", rules: "", gdpr: "" };

/**
 * Default public links for a race series.
 * @param {string} seriesKey - Race series key (b7, lh24, grizzly)
 * @returns {{ url: string, logoUrl: string, rules: string, gdpr: string }}
 */
export function getSeriesDefaultLinks(seriesKey) {
  return RaceSeriesDefaultLinks[seriesKey?.toLowerCase()] ?? emptySeriesDefaultLinks;
}

/**
 * Rules and GDPR URLs shown for a race. Stored links win; series defaults fill the gaps.
 * @param {{ series?: string, rulesLink?: string, gdprLink?: string }} [race]
 * @returns {{ rules: string, gdpr: string }}
 */
export function resolveRaceLegalLinks(race) {
  const defaults = getSeriesDefaultLinks(race?.series);
  return {
    rules: race?.rulesLink || defaults.rules,
    gdpr: race?.gdprLink || defaults.gdpr,
  };
}

export const RaceTypes = {
  solo: { key: "solo", name: "Jednotlivci" },
  team: { key: "team", name: "Týmy" },
};

export const Sexes = {
  male: {
    name: "Muž",
  },
  female: {
    name: "Žena",
  },
};

export const CategorySexes = {
  male: { name: "Muži" },
  female: { name: "Ženy" },
  mixed: { name: "Mix" },
};

export const RaceStates = {
  ACTIVE: "active",
  PENDING_FINALIZATION: "pendingFinalization",
  FINALIZED: "finalized",
  LOTTERY_ONE_PENDING_DRAW: "lotteryOnePendingDraw",
  LOTTERY_ONE_DRAWN: "lotteryOneDrawn",
  LOTTERY_TWO_PENDING_DRAW: "lotteryTwoPendingDraw",
  LOTTERY_TWO_DRAWN: "lotteryTwoDrawn",
};

export const RaceStateLabels = {
  [RaceStates.ACTIVE]: "Aktivní",
  [RaceStates.PENDING_FINALIZATION]: "Probíhá ukončení registrace",
  [RaceStates.FINALIZED]: "Registrace ukončena",
  [RaceStates.LOTTERY_ONE_PENDING_DRAW]: "Probíhá losování 1",
  [RaceStates.LOTTERY_ONE_DRAWN]: "Kolo 1 vylosováno",
  [RaceStates.LOTTERY_TWO_PENDING_DRAW]: "Probíhá losování 2",
  [RaceStates.LOTTERY_TWO_DRAWN]: "Kolo 2 vylosováno",
};

export const FinishRegistrationInputTypes = {
  MANUAL: "manual",
  PLANNED: "planned",
};

export const FinishRegistrationInputTypeLabels = {
  [FinishRegistrationInputTypes.MANUAL]: "Ruční",
  [FinishRegistrationInputTypes.PLANNED]: "Plánované",
};

export const LotteryTypes = {
  ONE: "one",
  TWO: "two",
};

export const DefaultMinimumAge = 18;

const BeskydskaSedmickaSoloCategories = [
  { label: "Muži 18–24", sex: "male", ageRange: [18, 24] },
  { label: "Muži 25–34", sex: "male", ageRange: [25, 34] },
  { label: "Muži 35–44", sex: "male", ageRange: [35, 44] },
  { label: "Muži 45–54", sex: "male", ageRange: [45, 54] },
  { label: "Muži 55–64", sex: "male", ageRange: [55, 64] },
  { label: "Muži 65–74", sex: "male", ageRange: [65, 74] },
  { label: "Muži 75+", sex: "male", ageRange: [75, 999] },
  { label: "Ženy 18–24", sex: "female", ageRange: [18, 24] },
  { label: "Ženy 25–34", sex: "female", ageRange: [25, 34] },
  { label: "Ženy 35–44", sex: "female", ageRange: [35, 44] },
  { label: "Ženy 45–54", sex: "female", ageRange: [45, 54] },
  { label: "Ženy 55–64", sex: "female", ageRange: [55, 64] },
  { label: "Ženy 65–74", sex: "female", ageRange: [65, 74] },
  { label: "Ženy 75+", sex: "female", ageRange: [75, 999] },
];

const LysaHoraSoloCategories = [
  { label: "Muži 18–35", sex: "male", ageRange: [18, 35] },
  { label: "Muži 36–50", sex: "male", ageRange: [36, 50] },
  { label: "Muži 51+", sex: "male", ageRange: [51, 999] },
  { label: "Ženy 18–35", sex: "female", ageRange: [18, 35] },
  { label: "Ženy 36–50", sex: "female", ageRange: [36, 50] },
  { label: "Ženy 51+", sex: "female", ageRange: [51, 999] },
];

const LysaHoraTeamCategories = [
  { label: "Muži 18–35", sex: "male", ageRange: [18, 35] },
  { label: "Muži 36–50", sex: "male", ageRange: [36, 50] },
  { label: "Muži 51+", sex: "male", ageRange: [51, 999] },
  { label: "Ženy 18–35", sex: "female", ageRange: [18, 35] },
  { label: "Ženy 36–50", sex: "female", ageRange: [36, 50] },
  { label: "Ženy 51+", sex: "female", ageRange: [51, 999] },
  { label: "Mix 18–35", sex: "mixed", ageRange: [18, 35] },
  { label: "Mix 36–50", sex: "mixed", ageRange: [36, 50] },
  { label: "Mix 51+", sex: "mixed", ageRange: [51, 999] },
];

const GrizzlymanSoloCategories = [
  { label: "Muži 18–29", sex: "male", ageRange: [18, 29] },
  { label: "Muži 30–39", sex: "male", ageRange: [30, 39] },
  { label: "Muži 40–49", sex: "male", ageRange: [40, 49] },
  { label: "Muži 50–59", sex: "male", ageRange: [50, 59] },
  { label: "Muži 60+", sex: "male", ageRange: [60, 999] },
  { label: "Ženy 18–29", sex: "female", ageRange: [18, 29] },
  { label: "Ženy 30–39", sex: "female", ageRange: [30, 39] },
  { label: "Ženy 40–49", sex: "female", ageRange: [40, 49] },
  { label: "Ženy 50–59", sex: "female", ageRange: [50, 59] },
  { label: "Ženy 60+", sex: "female", ageRange: [60, 999] },
];

const GrizzlymanTeamCategories = [
  { label: "Tým", sex: "male", ageRange: [18, 999] },
  { label: "Tým", sex: "female", ageRange: [18, 999] },
  { label: "Tým", sex: "mixed", ageRange: [18, 999] },
];

export const DefaultRaceCategories = {
  b7: { solo: BeskydskaSedmickaSoloCategories, team: BeskydskaSedmickaSoloCategories },
  lh24: { solo: LysaHoraSoloCategories, team: LysaHoraTeamCategories },
  grizzly: { solo: GrizzlymanSoloCategories, team: GrizzlymanTeamCategories },
};

/**
 * Category preset for a new race.
 * @param {string} seriesKey - Race series key (b7, lh24, grizzly)
 * @param {string} raceType - Race type key (solo, team)
 * @returns {Array<{ label: string, sex: string, ageRange: number[] }>} Preset categories, or an empty list
 */
export function getDefaultRaceCategories(seriesKey, raceType) {
  return DefaultRaceCategories[seriesKey?.toLowerCase()]?.[raceType] ?? [];
}
